import { useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { classNames } from "primereact/utils";

export default function ReportListSection({
    reports,
    sidebarPinned,
    setSidebarPinned,
    setSidebarHover,
    setGroupByOptions,
    setGroupBySelected,
    setSelectedReportPath,
    setReportType,
    onSelectReport
}) {

    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState({});
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [selectedChildName, setSelectedChildName] = useState(null);

    const toggleGroup = (id) => {
        setExpanded(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    /* ---------- FORMAT TITLE ---------- */

    const toTitleCase = (text) => {
        return text
            .replace(/^\d+\s*\.\s*/, "")
            .toLowerCase()
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    /* ---------- GROUP CLICK ---------- */

    const handleGroupClick = (group) => {

        setSelectedGroupId(group.id);
        setSelectedChildName(null);

        const tagSet = new Set();

        group.children.forEach(child => {

            if (!child.groupByTag) return;

            child.groupByTag.split(",").forEach(tag => {
                tagSet.add(tag.trim());
            });

        });

        const options = Array.from(tagSet).map(tag => ({
            label: tag,
            value: tag
        }));

        setGroupByOptions(options);
        setGroupBySelected([]);
        setReportType("summary");

        setSelectedReportPath(toTitleCase(group.title));
    };

    /* ---------- CHILD CLICK ---------- */

    const handleChildClick = (group, child) => {

        setSelectedGroupId(group.id);
        setSelectedChildName(child.name);

        const tags = child.groupByTag?.split(",").map(t => t.trim()) || [];

        const selected = tags.map(tag => ({
            label: tag,
            value: tag
        }));

        setGroupBySelected(selected);
        const childType = (child.reportType || "Summary").toLowerCase();
        setReportType(childType === "s" ? "summary" : childType === "d" ? "detail" : childType);

        setSelectedReportPath(
            `${toTitleCase(group.title)} → ${toTitleCase(child.name)}`
        );

        if (onSelectReport) {
            onSelectReport(child);
        }
    };

    /* ---------- SEARCH ---------- */

    const filteredReports = useMemo(() => {

        if (!search.trim()) return reports;

        const newExpanded = {};

        const result = reports
            .map(group => {

                const matchedChildren = group.children.filter(c =>
                    c.name.toLowerCase().includes(search.toLowerCase())
                );

                if (matchedChildren.length > 0) {

                    newExpanded[group.id] = true;

                    return {
                        ...group,
                        children: matchedChildren
                    };

                }

                return null;

            })
            .filter(Boolean);

        setExpanded(newExpanded);

        return result;

    }, [search, reports]);

    return (

        <div
            className="report-sidebar"
            onMouseEnter={() => setSidebarHover(true)}
            onMouseLeave={() => setSidebarHover(false)}
        >

            <Card className="report-card">

                <Tooltip
                    target=".pin-icon"
                    content={sidebarPinned ? "Unpin" : "Pin"}
                    position="bottom"
                />

                {/* HEADER */}

                <div className="report-list-header">

                    <span className="report-title">
                        <i className="pi pi-chart-bar mr-2"></i>
                        Report List
                    </span>

                    <i
                        className={classNames(
                            "pi pi-thumbtack pin-icon",
                            { pinned: sidebarPinned }
                        )}
                        onClick={() => setSidebarPinned(prev => !prev)}
                    />

                </div>

                {/* SEARCH */}

                <div className="report-list-search">

                    <InputText
                        placeholder="Search reports..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="p-inputtext-sm"
                    />

                    <Button
                        icon="pi pi-search"
                        className="btn-primary btn-reportSearch"
                    />

                </div>

                {/* REPORT LIST */}

                <div className="report-list-body">

                    <div className="group-title">
                        Group Title
                    </div>

                    {filteredReports.map((group) => (

                        <div key={group.id} className="report-group">

                            {/* GROUP HEADER */}

                            <div
                                className={classNames("group-header", {
                                    "group-active": selectedGroupId === group.id
                                })}
                                onClick={() => {
                                    toggleGroup(group.id);
                                    handleGroupClick(group);
                                }}
                            >

                                <span>
                                    <i className="pi pi-chart-line mr-2 group-icon"></i>
                                    {group.title}
                                </span>

                                <i
                                    className={classNames(
                                        "pi",
                                        expanded[group.id]
                                            ? "pi-angle-down"
                                            : "pi-angle-right"
                                    )}
                                />

                            </div>

                            {/* CHILD REPORTS */}

                            {expanded[group.id] && (

                                <ul className="report-items">

                                    {group.children.map((child, index) => (

                                        <li
                                            key={index}
                                            className={classNames("report-item", {
                                                "report-item-active":
                                                    selectedChildName === child.name
                                            })}
                                            onClick={() => handleChildClick(group, child)}
                                        >

                                            <i className="pi pi-table mr-2 report-icon"></i>

                                            {child.name}

                                        </li>

                                    ))}

                                </ul>

                            )}

                        </div>

                    ))}

                </div>

            </Card>

        </div>

    );
}